import React, { useState } from 'react';

import { IconExclamationCircle } from '@tabler/icons-react';
import iconPath from 'assets/pngs/partner-referral.png';
import subTextImage from 'assets/pngs/stratify.png';
import closeIcon from 'assets/svgs/close-gray.svg';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { Dialog, useDialog } from 'ui-kit';
import * as Yup from 'yup';

import { gql, useMutation } from '@apollo/client';

const SUBMIT_REFERRAL_MUTATION = gql`
  mutation SubmitReferral($input: SubmitReferralInput!) {
    submitReferral(input: $input)
  }
`;

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First Name is required'),
  lastName: Yup.string().required('Last Name is required'),
  institution: Yup.string().required('Institution is required'),
  emailAddress: Yup.string().email('Invalid email').required('Email is required'),
  phone: Yup.string().required('Phone is required'),
});

const ReferralLink = () => {
  const referralDialog = useDialog();

  const [submitReferral] = useMutation(SUBMIT_REFERRAL_MUTATION);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  return (
    <div>
      <ReferralLinkButton
        onClick={() => {
          setErrorMessage(null);
          referralDialog.setIsOpen(true);
        }}
      >
        Refer A Colleague
      </ReferralLinkButton>

      <Dialog dialog={referralDialog} contentComponent={ReferralContentDiv}>
        <CloseButton onClick={() => referralDialog.closeDialog()} />
        <div className="gap-x-15 mt-4 flex flex-nowrap">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: 320,
            }}
          >
            <img src={iconPath} width={233} height={232} alt="refer a colleague" />
            <img src={subTextImage} width={348} height={74} alt="sub text" />
            <Title>Refer colleagues to buy and sell loans on the ORSNN platform.</Title>
          </div>
          <div>
            <Title classNames="text-left">Referral Link & Code</Title>
            <Formik
              initialValues={{
                firstName: '',
                lastName: '',
                emailAddress: '',
                phone: '',
                institution: '',
              }}
              validationSchema={validationSchema}
              onSubmit={(values) => {
                submitReferral({
                  variables: {
                    input: {
                      firstname: values.firstName,
                      lastname: values.lastName,
                      email: values.emailAddress,
                      phone: values.phone,
                      company: values.institution,
                    },
                  },
                })
                  .then(({ data }) => {
                    if (data.submitReferral) {
                      // The referral was successfully submitted
                      referralDialog.closeDialog();
                    } else {
                      // The referral was not successfully submitted
                      setErrorMessage('An error occurred, please try again later.');
                    }
                  })
                  .catch((error) => {
                    // An error occurred while submitting the referral
                    console.error('error: ' + error.message);
                    // Set the error message
                    setErrorMessage('An error occurred, please try again later.');
                  });
              }}
            >
              <Form>
                <div className="mt-4 flex flex-nowrap gap-x-3">
                  <FormRow label="First Name *">
                    <div>
                      <Field id="firstName" name="firstName" />
                      <CustomErrorMessage name="firstName" />
                    </div>
                  </FormRow>

                  <FormRow label="Last Name *">
                    <label htmlFor="lastName">Last Name *</label>
                    <Field id="lastName" name="lastName" />
                    <CustomErrorMessage name="lastName" />
                  </FormRow>
                </div>

                <FormRow label="Institution *">
                  <Field id="institution" name="institution" />
                  <CustomErrorMessage name="institution" />
                </FormRow>

                <FormRow label="Email Address *">
                  <div>
                    <Field id="emailAddress" name="emailAddress" />
                    <CustomErrorMessage name="emailAddress" />
                  </div>
                </FormRow>

                <FormRow label="Phone *">
                  <div>
                    <Field id="phone" name="phone" />
                    <CustomErrorMessage name="phone" />
                  </div>
                </FormRow>

                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <button className="mt-[15px] rounded bg-accent-emphasis px-4 py-2 text-white transition hover:bg-accent-default">
                    Send Referral Invitation
                  </button>
                </div>

                {errorMessage && (
                  <div style={{ paddingTop: 30, textAlign: 'left' }}>{errorMessage}</div>
                )}
              </Form>
            </Formik>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export const Title = ({
  children,
  classNames = '',
}: {
  children: React.ReactNode;
  classNames?: string;
}) => (
  <div className={`mb-[10px] font-[Heebo] text-[24px] font-medium leading-[36px] ${classNames}`}>
    {children}
  </div>
);

interface ReferralLinkButtonProps {
  onClick?: () => void;
  children: React.ReactNode;
}

export const ReferralLinkButton = ({ onClick, children }: ReferralLinkButtonProps) => (
  <button
    onClick={onClick}
    className="
   fixed right-[-47px] top-[130px]
   z-[1000]
   rotate-[-90deg]
   cursor-pointer
   rounded-t-md border
   border-accent-emphasis bg-[#1f1f1f]
   px-3
   py-2
   font-[Heebo]
   text-accent-emphasis
   hover:text-accent-default
  "
  >
    {children}
  </button>
);

export const ReferralContentDiv = ({ children }: { children: React.ReactNode }) => (
  <div className="border-accent-emphasisbg-[#131316]pb-10 relative rounded-lg border pl-10 pr-10 pt-[60px]">
    {children}
  </div>
);

interface FormRowProps {
  label?: string;
  children: React.ReactNode;
}

export const FormRow = ({ label, children }: FormRowProps) => (
  <div className="mb-5 w-full">
    {label && (
      <label className="mb-1 block text-left text-sm font-normal text-slate-200">{label}</label>
    )}
    {children}
  </div>
);

export default ReferralLink;

export const CloseButton = ({ onClick }: { onClick: () => void }) => (
  <button
    className="
   bg-transparent absolute right-4
   top-[10px] h-6
   w-6 cursor-pointer
   border-none bg-cover
  "
    style={{ backgroundImage: `url(${closeIcon})` }}
    onClick={onClick}
  />
);

const CustomErrorMessage = ({ name }: { name: string }) => (
  <ErrorMessage name={name}>
    {(errorMessage) => (
      <div
        style={{
          color: '#e95c7b',
          fontSize: '11px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div style={{ position: 'relative', left: '-2px', top: '1px' }}>
          <IconExclamationCircle size={16} color="#E95C7B" />
        </div>
        {errorMessage}
      </div>
    )}
  </ErrorMessage>
);
